import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import './SubscriptionAccounts.css'
import { SubscriptionAccount, Subscription, Filter } from '../store/store'
import slugify from 'react-slugify'
import { RequestState } from '../../types'
import DemoAccountIndicator from '../../accounts/components/DemoAccountIndicator'
import WarningText from '../../../components/text/WarningText'
import AlertText from '../../../components/text/AlertText'
import Date from '../../../components/Date'
import { useHistory } from 'react-router-dom'

type Props = {
  subscription: Subscription
  loadSubscriptionAccountFilters: (subscriptionId: string, accountId: string) => Promise<void>
}

function SubscriptionAccounts(props: Props) {
  const { subscription, loadSubscriptionAccountFilters } = props
  const { t } = useTranslation(['accounts', 'global', 'tooltips'])
  const history = useHistory()

  useEffect(() => {
    if (subscription.accounts.length > 0) {
      subscription.accounts.forEach((subscriptionAccount: SubscriptionAccount) => {
        if (subscriptionAccount.requestState === RequestState.NotLoaded && subscriptionAccount.active) {
          loadSubscriptionAccountFilters(subscription.id, subscriptionAccount.id)
        }
      })
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscription.accounts])

  return (
    <>
      {subscription.requestState === RequestState.Loading && subscription.accounts.length === 0 &&
        <div>
          <i className="fas fa-spinner fa-spin"></i>&nbsp;
          <span>{t('global:loading')}</span>
        </div>
      }

      {subscription.requestState !== RequestState.Loading && subscription.accounts.length === 0 &&
        <div>
          {t('no_accounts')}
        </div>
      }

      {subscription.accounts.length > 0 &&
        <table id="accounts" className="accounts" cellSpacing={0}>
          <thead>
            <tr>
              <td className="account-name">
                <b>{t('fields.name')}</b>
              </td>
              <td className="account-status">
                <b>{t('fields.last_activity')}</b>
              </td>
              <td className="account-records">
                <b>{t('fields.records')}</b>
              </td>
              <td className="account-filters-warn">
                {t('filtersWarn')}
              </td>
              <td className="account-filters-alert">
                {t('filtersAlert')}
              </td>
              <td>
              </td>
            </tr>
          </thead>
          <tbody>
            {subscription.accounts.map((account: SubscriptionAccount) => (
              <tr id={slugify(account.name)} data-cy={slugify(account.name)} key={account.id}>
                <td>
                  <span data-cy="name" className={"account-name" + (!account.active ? ' inactive' : '')}>
                    {account.name}
                  </span>
                  <DemoAccountIndicator isDemo={account.isDemo} accountActive={account.active} />
                </td>
                <td data-cy="status" className="field account-status">
                  {!account.active ? t('deactivated') :
                    account.lastActive
                      ? <Date date={account.lastActive as Date} />
                      : t('no_activity')
                  }
                </td>
                <td data-cy="records" className={"field account-records" + (!account.active ? ' inactive' : '')}>
                  {account.records}
                </td>
                <td data-cy="filters-alert" className="field account-filters-alert">
                  {account.active && account.filters &&
                    account.filters.filter((filter: Filter) => filter.profile === 'alert').map((filter: Filter) => (
                      <div key={filter.id}>
                        {filter.count > 0 && (
                          <AlertText text={`${filter.count} ${filter.name}`} />
                        )}
                      </div>
                    ))
                  }
                </td>
                <td data-cy="filters-warn" className="field account-filters-warn">
                  {account.active && account.filters && account.filters
                    .filter((f: Filter) => f.profile === 'warn')
                    .map((filter: Filter) => (
                      <div key={filter.id}>
                        {filter.count > 0 && (
                          <WarningText text={`${filter.count} ${filter.name}`} />
                        )}
                      </div>
                    ))}
                </td>
                <td className="actions">
                  <a 
                    href={`${process.env.REACT_APP_WEPAPP_URL}/accounts/${account.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t('tooltips:open_account_webapp')}>
                    <i className="far fa-window-maximize icon-button web-expand"></i>
                  </a>
                  <span
                    title={t('tooltips:open_account_settings')}
                    data-cy="btn-account-admin" onClick={() => history.push(`/account/${account.id}/settings`)}>
                    <i className="fas fa-cog icon-button"></i>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    </>
  )
}

export default SubscriptionAccounts
